import { HttpBackend, HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { AppService } from "../../../core/services/app.service";

export interface SendPreviewAccessCodeRequest {
  email: string;
  digitalMemoryLogId: number;
}

export interface VerifyPreviewAccessCodeRequest {
  email: string;
  code: string;
  digitalMemoryLogId: number;
}

export interface PreviewAccessVerifyResponse {
  valid?: boolean;
  data?: any;
  accessToken?: string;
  token?: string;
  expiresAt?: string;
  digitalMemoryLog?: any;
  preview?: any;
  [key: string]: any;
}

/**
 * Paid-access OTP gate (post-payment email link / "Already paid?").
 * On verify success, BE returns digital memory log detail used to open Preview.
 */
@Injectable({
  providedIn: "root",
})
export class PreviewAccessService {
  private apiUrl = AppService.getApiUrl() + "/api/VerifyDMLs";
  private rawHttp: HttpClient;
  private readonly storagePrefix = "previewAccessVerified:";
  private readonly detailPrefix = "previewAccessDetail:";

  constructor(private http: HttpClient, httpBackend: HttpBackend) {
    this.rawHttp = new HttpClient(httpBackend);
  }

  /**
   * Absolute URL for BE payment-success email.
   * Format: {origin}/preview-{previewName}-{previewId}/access
   */
  static buildAccessUrl(previewName: string, previewId: string | number, origin?: string): string {
    const safeName = (previewName || "digital-memory").trim() || "digital-memory";
    const base = (origin || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/+$/, "");
    return `${base}/preview-${safeName}-${previewId}/access`;
  }

  /** POST /api/VerifyDMLs/send — send OTP to email */
  sendAccessCode(payload: SendPreviewAccessCodeRequest): Observable<any> {
    const url = this.apiUrl + "/send";
    return this.rawHttp.post(url, payload).pipe(
      map((res: any) => {
        if (res && res.IsSuccess === false) {
          throw res;
        }
        return (res && res.Data) || res;
      }),
      catchError(error => throwError(error))
    );
  }

  /** GET /api/VerifyDMLs/verify?email=&code=&digitalMemoryLogId= */
  verifyAccessCode(payload: VerifyPreviewAccessCodeRequest): Observable<PreviewAccessVerifyResponse> {
    const url = this.apiUrl + "/verify";
    const params = {
      email: payload.email,
      code: payload.code,
      digitalMemoryLogId: String(payload.digitalMemoryLogId),
    };
    return this.rawHttp.get(url, { params: params }).pipe(
      map((res: any) => {
        if (res && res.IsSuccess === false) {
          throw res;
        }
        return (res && res.Data) || res;
      }),
      catchError(error => throwError(error))
    );
  }

  /**
   * BE shape: Data = { valid: true, data: <digitalMemoryLog> }
   */
  extractPreviewDetail(response: PreviewAccessVerifyResponse | null | undefined): any | null {
    if (!response) {
      return null;
    }

    if (response.valid === false) {
      return null;
    }

    // Primary contract: { valid: true, data: { ...dml } }
    if (response.data) {
      return response.data;
    }

    if (response.digitalMemoryLog) {
      return response.digitalMemoryLog;
    }
    if (response.preview) {
      return response.preview;
    }
    if (response["DigitalMemoryLog"]) {
      return response["DigitalMemoryLog"];
    }

    // Payload is already the DML detail (same shape as getPreviewById).
    if (response.id != null || response.wallpapers || response.photos || response.videos || response.name) {
      return response;
    }

    return null;
  }

  isVerifyValid(response: PreviewAccessVerifyResponse | null | undefined): boolean {
    if (!response) {
      return false;
    }
    if (response.valid === false) {
      return false;
    }
    // Accept missing `valid` if detail payload is present (older/fallback shapes).
    return response.valid === true || !!this.extractPreviewDetail(response);
  }

  markVerified(previewId: string, email: string, accessToken?: string, detail?: any): void {
    if (!previewId || typeof sessionStorage === "undefined") {
      return;
    }
    sessionStorage.setItem(
      this.storagePrefix + previewId,
      JSON.stringify({
        email: email,
        accessToken: accessToken || "",
        verifiedAt: new Date().toISOString(),
      })
    );

    if (detail) {
      this.setVerifiedDetail(previewId, detail);
    }
  }

  setVerifiedDetail(previewId: string, detail: any): void {
    if (!previewId || !detail || typeof sessionStorage === "undefined") {
      return;
    }
    try {
      sessionStorage.setItem(
        this.detailPrefix + previewId,
        JSON.stringify({
          data: detail,
          timestamp: Date.now(),
        })
      );
    } catch (error) {
      console.error("Error saving verified preview detail:", error);
    }
  }

  getVerifiedDetail(previewId: string): any | null {
    if (!previewId || typeof sessionStorage === "undefined") {
      return null;
    }
    try {
      const stored = sessionStorage.getItem(this.detailPrefix + previewId);
      if (!stored) {
        return null;
      }
      const parsed = JSON.parse(stored);
      const maxAge = 24 * 60 * 60 * 1000;
      if (Date.now() - parsed.timestamp > maxAge) {
        this.clearVerified(previewId);
        return null;
      }
      return parsed.data || null;
    } catch (error) {
      console.error("Error reading verified preview detail:", error);
      return null;
    }
  }

  isVerified(previewId: string): boolean {
    if (!previewId || typeof sessionStorage === "undefined") {
      return false;
    }
    return !!sessionStorage.getItem(this.storagePrefix + previewId);
  }

  clearVerified(previewId: string): void {
    if (!previewId || typeof sessionStorage === "undefined") {
      return;
    }
    sessionStorage.removeItem(this.storagePrefix + previewId);
    sessionStorage.removeItem(this.detailPrefix + previewId);
  }
}
